import { CoverTemplate, CoverTemplateValues } from '../types';
import { getDefaultCoverValues, buildDocumentFromCoverSections } from './templateUtils';
import { DocumentSection, TextBlock, ImageBlock, TableBlock } from '../../core/ast/document.types';

export const projectCoverTemplate: CoverTemplate = {
  id: 'tpl-project-capstone',
  name: 'Capstone Project / Thesis Cover',
  category: 'project',
  description: 'Formal comprehensive cover design for final year design projects, capstone thesis submissions, and group term projects.',
  thumbnailBadge: 'Project / Thesis',
  version: 3,
  supportedFields: [
    'universityName', 'universityAddress', 'departmentName', 'logoAssetId', 'showLogo',
    'documentTypeLabel', 'title', 'subtitle',
    'courseCode', 'courseTitle', 'groupMembers',
    'studentName', 'studentId', 'studentEmail',
    'facultyName', 'facultyDesignation', 'supervisorName',
    'submissionDate', 'semester', 'academicYear',
  ],
  defaultValues: getDefaultCoverValues({
    documentTypeLabel: 'CAPSTONE DESIGN PROJECT REPORT',
    title: '',
    subtitle: '',
    courseCode: '',
    courseTitle: '',
    groupMembers: '',
    studentName: '',
    studentId: '',
    studentEmail: '',
    facultyName: '',
    facultyDesignation: '',
    supervisorName: '',
    submissionDate: '',
    borderStyle: 'single',
  }),
  compileToDocument: (values: CoverTemplateValues, pageSettings) => {
    const now = new Date().toISOString();
    const blocks: Array<TextBlock | ImageBlock | TableBlock> = [];

    // 1. Center Logo
    if (values.showLogo && values.logoAssetId) {
      blocks.push({
        id: 'blk-cover-logo',
        type: 'image',
        createdAt: now,
        data: {
          assetId: values.logoAssetId,
          previewDataUrl: values.logoAssetId,
          widthPercent: 26,
          alignment: 'center',
          altText: values.universityName || 'East West University',
        },
      });
    }

    // 2. University Name (Center Aligned)
    blocks.push({
      id: 'blk-cover-univ',
      type: 'text',
      createdAt: now,
      data: {
        text: (values.universityName || 'EAST WEST UNIVERSITY').toUpperCase(),
        formatting: { bold: true, fontSizePt: 16, alignment: 'center', color: '#111827' },
      },
    });

    // 3. Address (Center Aligned)
    blocks.push({
      id: 'blk-cover-addr',
      type: 'text',
      createdAt: now,
      data: {
        text: values.universityAddress || 'A/2 Jahurul Islam Ave, Dhaka 1212',
        formatting: { fontSizePt: 10, alignment: 'center', color: '#4a5563' },
      },
    });

    // Spacing
    blocks.push({
      id: 'blk-cover-sp1',
      type: 'text',
      createdAt: now,
      data: { text: '\n\n', formatting: { fontSizePt: 12 } },
    });

    // 4. Badge
    if (values.documentTypeLabel) {
      blocks.push({
        id: 'blk-cover-badge',
        type: 'text',
        createdAt: now,
        data: {
          text: `— ${values.documentTypeLabel} —`,
          formatting: { bold: true, fontSizePt: 12, alignment: 'center', color: '#16a34a' },
        },
      });
    }

    // 5. Project Title & Subtitle
    if (values.title) {
      blocks.push({
        id: 'blk-cover-title',
        type: 'text',
        createdAt: now,
        data: {
          text: values.title,
          formatting: { bold: true, fontSizePt: 22, alignment: 'center', color: '#111827' },
        },
      });
    }

    if (values.subtitle) {
      blocks.push({
        id: 'blk-cover-sub',
        type: 'text',
        createdAt: now,
        data: {
          text: values.subtitle,
          formatting: { italic: true, fontSizePt: 12, alignment: 'center', color: '#4b5563' },
        },
      });
    }

    // Spacing
    blocks.push({
      id: 'blk-cover-sp2',
      type: 'text',
      createdAt: now,
      data: { text: '\n', formatting: { fontSizePt: 10 } },
    });

    // 6. Degree Submission Notice
    blocks.push({
      id: 'blk-cover-notice',
      type: 'text',
      createdAt: now,
      data: {
        text: `A report submitted in partial fulfillment of the requirements for the degree of\nBachelor of Science in Computer Science & Engineering`,
        formatting: { italic: true, fontSizePt: 10.5, alignment: 'center', color: '#6b7280' },
      },
    });

    // Spacing
    blocks.push({
      id: 'blk-cover-sp3',
      type: 'text',
      createdAt: now,
      data: { text: '\n', formatting: { fontSizePt: 10 } },
    });

    // 7. Project Team & Supervisor Grid
    blocks.push({
      id: 'blk-cover-proj-grid',
      type: 'table',
      createdAt: now,
      data: {
        rows: 2,
        cols: 2,
        hasHeaderRow: true,
        hasHeaderCol: false,
        borderStyle: 'academic',
        cells: [
          [
            { id: 'c00', content: 'PROJECT TEAM MEMBERS', isHeader: true, bold: true, alignment: 'left' },
            { id: 'c01', content: 'PROJECT SUPERVISOR', isHeader: true, bold: true, alignment: 'left' },
          ],
          [
            {
              id: 'c10',
              content: values.groupMembers || `Name: ${values.studentName || ''}\nID: ${values.studentId || ''}`,
              alignment: 'left',
            },
            {
              id: 'c11',
              content: `Supervisor: ${values.supervisorName || values.facultyName || ''}\nDesignation: ${values.facultyDesignation || ''}\nDepartment: ${values.facultyDepartment || values.departmentName || ''}`,
              alignment: 'left',
            },
          ],
        ],
      },
    });

    // 8. Submission Date
    if (values.submissionDate || values.academicYear) {
      blocks.push({
        id: 'blk-cover-date',
        type: 'text',
        createdAt: now,
        data: {
          text: `\nDate of Submission: ${values.submissionDate || ''}\nAcademic Year: ${values.academicYear || ''}`,
          formatting: { bold: true, fontSizePt: 10.5, alignment: 'center', color: '#374151' },
        },
      });
    }

    const coverSection: DocumentSection = {
      id: 'sec-cover-project',
      title: 'Cover Page',
      type: 'cover',
      enabled: true,
      collapsed: false,
      blocks,
    };

    return buildDocumentFromCoverSections(values, [coverSection], pageSettings);
  },
};
